/*
 * Copyright (c) 2021 - 2026 All rights reserved by Ferenc Farkas <wolferi93@gmail.com>
 */

/**
 * Pure, locale-aware value formatters for the insight widgets (money, counts,
 * percent, hours, deltas). Re-exported from `insightWidgets.js`.
 */

const LOCALE_SEPARATORS = {
    hu: { decimal: ',', group: ' ' },
    de: { decimal: ',', group: '.' },
    ro: { decimal: ',', group: '.' },
    svn: { decimal: ',', group: '.' },
    en: { decimal: '.', group: ',' },
    cn: { decimal: '.', group: ',' },
};

const COMPACT_SUFFIX = {
    hu: { k: 'E', m: 'M', b: 'Mrd' },
    de: { k: 'Tsd.', m: 'Mio.', b: 'Mrd.' },
    ro: { k: 'K', m: 'M', b: 'Mld.' },
    svn: { k: 'tis.', m: 'mio.', b: 'mrd.' },
    en: { k: 'K', m: 'M', b: 'B' },
    cn: { k: 'K', m: 'M', b: 'B' },
};

const CURRENCY_SYMBOLS = { HUF: 'Ft', EUR: '€', USD: '$', GBP: '£', RON: 'lei', CHF: 'CHF', CNY: '¥' };
const PREFIX_CURRENCIES = ['USD', 'GBP', 'CNY'];

/**
 * Normalize a locale tag to one of the six supported ids (default `hu`).
 * @param {string} [locale]
 * @returns {'hu'|'en'|'de'|'ro'|'cn'|'svn'}
 */
export const normalizeInsightLocale = (locale) => {
    const tag = String(locale || 'hu').toLowerCase();
    if (tag.startsWith('zh')) {
        return 'cn';
    }
    if (tag.startsWith('sl')) {
        return 'svn';
    }
    if (LOCALE_SEPARATORS[tag]) {
        return tag;
    }
    const two = tag.slice(0, 2);

    return LOCALE_SEPARATORS[two] ? two : 'hu';
};

/**
 * Format a number with grouping and at most `digits` decimals (trailing zero
 * decimals are dropped).
 * @param {number} value
 * @param {string} [locale]
 * @param {number} [digits]
 * @returns {string}
 */
export const formatNumber = (value, locale = 'hu', digits = 0) => {
    const num = Number(value);
    if (!Number.isFinite(num)) {
        return '–';
    }
    const sep = LOCALE_SEPARATORS[normalizeInsightLocale(locale)];
    const fixed = Math.abs(num).toFixed(digits);
    const [int, frac] = fixed.split('.');
    const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, sep.group);
    const body = frac && Number(frac) !== 0 ? `${grouped}${sep.decimal}${frac}` : grouped;

    return num < 0 && Number(fixed) !== 0 ? `-${body}` : body;
};

/**
 * Compact magnitude: 1 234 567 → "1,2 M".
 * @param {number} value
 * @param {string} [locale]
 * @returns {string}
 */
export const formatCompact = (value, locale = 'hu') => {
    const num = Number(value);
    if (!Number.isFinite(num)) {
        return '–';
    }
    const loc = normalizeInsightLocale(locale);
    const suffix = COMPACT_SUFFIX[loc];
    const abs = Math.abs(num);
    let scaled = num;
    let tag = '';
    if (abs >= 1e9) {
        scaled = num / 1e9;
        tag = suffix.b;
    } else if (abs >= 1e6) {
        scaled = num / 1e6;
        tag = suffix.m;
    } else if (abs >= 1e5) {
        scaled = num / 1e3;
        tag = suffix.k;
    }
    const digits = tag && Math.abs(scaled) < 10 ? 1 : 0;
    const text = formatNumber(scaled, loc, digits);

    return tag ? `${text} ${tag}` : text;
};

/**
 * Money, compact: "1,2 M Ft", "$3.4 K" by currency convention.
 * @param {number} value
 * @param {string} [currency] ISO code (default HUF).
 * @param {string} [locale]
 * @returns {string}
 */
export const formatMoney = (value, currency = 'HUF', locale = 'hu') => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return '–';
    }
    const code = String(currency || 'HUF').toUpperCase();
    const symbol = CURRENCY_SYMBOLS[code] || code;
    const body = formatCompact(value, locale);

    return PREFIX_CURRENCIES.includes(code) ? `${symbol}${body}` : `${body} ${symbol}`;
};

/**
 * Format a value by its backend `unit` (`currency|count|percent|hours|days|km|…`).
 * Percent values are 0..100.
 * @param {number|null} value
 * @param {{ unit?: string, currency?: string, locale?: string }} [meta]
 * @returns {string}
 */
export const formatInsightValue = (value, meta = {}) => {
    if (typeof value !== 'number' || !Number.isFinite(value)) {
        return '–';
    }
    const { unit, currency, locale } = meta;
    switch (unit) {
        case 'currency':
            return formatMoney(value, currency, locale);
        case 'percent':
            return `${formatNumber(value, locale, 1)}%`;
        case 'hours':
            return `${formatNumber(value, locale, 1)} h`;
        case 'days':
            return `${formatNumber(value, locale, 1)} d`;
        case 'km':
            return `${formatCompact(value, locale)} km`;
        case 'count':
        case undefined:
        case '':
            return formatCompact(value, locale);
        default:
            return `${formatCompact(value, locale)} ${unit}`;
    }
};

/**
 * Format a delta fraction as "+12%" / "-3.5%".
 * @param {number|null} fraction
 * @param {string} [locale]
 * @returns {string} Empty when not computable.
 */
export const formatDelta = (fraction, locale = 'hu') => {
    if (typeof fraction !== 'number' || !Number.isFinite(fraction)) {
        return '';
    }
    const pct = fraction * 100;
    const text = formatNumber(Math.abs(pct), locale, Math.abs(pct) < 10 ? 1 : 0);

    return `${pct >= 0 ? '+' : '-'}${text}%`;
};

/**
 * Turn a camelCase key into a readable fallback label ("openReceivables" → "Open receivables").
 * @param {string} key
 * @returns {string}
 */
export const humanizeKey = (key) => {
    const spaced = String(key || '')
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/[_-]+/g, ' ')
        .trim()
        .toLowerCase();

    return spaced ? spaced.charAt(0).toUpperCase() + spaced.slice(1) : '';
};
